// Canvas2D painting kit for the procedural boss parts.
// House style (art bible): chunky readable silhouettes, 2 px ink outline,
// upper-left key light (gradient + bevel highlight on top-left edges, shade on
// bottom-right edges), restrained panel detail, bright colour only on emissives.

import type Phaser from 'phaser';
import { COLORS, css, mix, shade } from '../../config/palette';
import { Rng } from '../../core/rng';

export type G = CanvasRenderingContext2D;
export type Pt = readonly [number, number];

export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Create a canvas texture once (keys that already exist — e.g. loaded art — are kept). */
export function canvasTex(scene: Phaser.Scene, key: string, w: number, h: number, draw: (g: G) => void): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.createCanvas(key, Math.ceil(w), Math.ceil(h));
  if (!tex) return;
  const g = tex.getContext();
  g.save();
  g.lineJoin = 'round';
  g.lineCap = 'round';
  draw(g);
  g.restore();
  tex.refresh();
}

// ───────────── paths ─────────────

export function poly(pts: readonly Pt[]): Path2D {
  const p = new Path2D();
  p.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) p.lineTo(pts[i][0], pts[i][1]);
  p.closePath();
  return p;
}

/** Rectangle with chamfered corners [tl, tr, br, bl]. */
export function chamfer(x: number, y: number, w: number, h: number, c: number | readonly [number, number, number, number]): Path2D {
  const [tl, tr, br, bl] = typeof c === 'number' ? [c, c, c, c] : c;
  return poly([
    [x + tl, y],
    [x + w - tr, y],
    [x + w, y + tr],
    [x + w, y + h - br],
    [x + w - br, y + h],
    [x + bl, y + h],
    [x, y + h - bl],
    [x, y + tl],
  ]);
}

export function disc(x: number, y: number, r: number): Path2D {
  const p = new Path2D();
  p.arc(x, y, r, 0, Math.PI * 2);
  return p;
}

export function ellipse(x: number, y: number, rx: number, ry: number, rot = 0): Path2D {
  const p = new Path2D();
  p.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
  return p;
}

/** Annular sector (ring segment) centred on (cx, cy). */
export function sector(cx: number, cy: number, r0: number, r1: number, a0: number, a1: number): Path2D {
  const p = new Path2D();
  p.arc(cx, cy, r1, a0, a1);
  p.arc(cx, cy, r0, a1, a0, true);
  p.closePath();
  return p;
}

export function bounds(pts: readonly Pt[]): Bounds {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [x, y] of pts) {
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

// ───────────── materials ─────────────

export interface SolidOpts {
  /** Brightness multiplier at the lit (upper-left) end of the gradient. */
  hi?: number;
  /** Brightness multiplier at the shaded (lower-right) end. */
  lo?: number;
  /** Bevel width in px (0 = flat). */
  bevel?: number;
  /** Draw the 2 px ink outline (default true). */
  outline?: boolean;
  /** Extra outline width (px outside the shape). */
  outlineWidth?: number;
}

/** Key-lit solid: ink outline, upper-left gradient, bevel highlight + shade. */
export function solid(g: G, path: Path2D, b: Bounds, base: number, o: SolidOpts = {}): void {
  const hi = o.hi ?? 1.38;
  const lo = o.lo ?? 0.58;
  const bevel = o.bevel ?? 2;
  if (o.outline !== false) {
    g.save();
    g.lineJoin = 'round';
    g.lineWidth = (o.outlineWidth ?? 2) * 2;
    g.strokeStyle = css(COLORS.ink);
    g.stroke(path);
    g.restore();
  }
  const grad = g.createLinearGradient(b.x, b.y, b.x + b.w * 0.75, b.y + b.h);
  grad.addColorStop(0, css(shade(base, hi)));
  grad.addColorStop(0.5, css(base));
  grad.addColorStop(1, css(shade(base, lo)));
  g.fillStyle = grad;
  g.fill(path);
  if (bevel > 0) bevelEdges(g, path, base, bevel);
}

/** Highlight along top-left edges, shade along bottom-right edges (clipped inside the path). */
export function bevelEdges(g: G, path: Path2D, base: number, w = 2, strength = 1): void {
  g.save();
  g.clip(path);
  g.lineJoin = 'round';
  g.lineWidth = w * 1.6;
  g.translate(w, w);
  g.strokeStyle = css(mix(shade(base, 1.9), 0xffffff, 0.15), 0.7 * strength);
  g.stroke(path);
  g.translate(-2 * w, -2 * w);
  g.strokeStyle = css(shade(base, 0.38), 0.75 * strength);
  g.stroke(path);
  g.restore();
}

/** Flat fill without lighting (insets, recesses). */
export function flat(g: G, path: Path2D, color: number, alpha = 1): void {
  g.fillStyle = css(color, alpha);
  g.fill(path);
}

/** Recessed socket: dark fill with an inner shadow on the top-left (light falls into it). */
export function recess(g: G, path: Path2D, color: number = COLORS.ink, depth = 3): void {
  g.fillStyle = css(color);
  g.fill(path);
  g.save();
  g.clip(path);
  g.lineWidth = depth * 2;
  g.translate(-depth * 0.6, -depth * 0.6);
  g.strokeStyle = 'rgba(0,0,0,0.55)';
  g.stroke(path);
  g.translate(depth * 1.4, depth * 1.4);
  g.strokeStyle = 'rgba(255,255,255,0.08)';
  g.stroke(path);
  g.restore();
}

/** Engraved panel seam: dark groove with a light lip on its lower-right side. */
export function groove(g: G, pts: readonly Pt[], base: number, width = 1.6): void {
  g.save();
  g.lineCap = 'round';
  g.lineJoin = 'round';
  const path = new Path2D();
  path.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) path.lineTo(pts[i][0], pts[i][1]);
  g.lineWidth = width;
  g.strokeStyle = css(shade(base, 0.35), 0.95);
  g.stroke(path);
  g.translate(1, 1);
  g.lineWidth = width * 0.7;
  g.strokeStyle = css(shade(base, 1.7), 0.35);
  g.stroke(path);
  g.restore();
}

export function rivets(g: G, pts: readonly Pt[], base: number, r = 2.4): void {
  for (const [x, y] of pts) {
    g.fillStyle = css(shade(base, 0.35));
    g.beginPath();
    g.arc(x + 0.6, y + 0.6, r + 0.6, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = css(shade(base, 1.15));
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = css(shade(base, 2.1), 0.85);
    g.beginPath();
    g.arc(x - r * 0.35, y - r * 0.35, r * 0.42, 0, Math.PI * 2);
    g.fill();
  }
}

/** Row of vent slots inside a box. */
export function vents(g: G, x: number, y: number, w: number, h: number, n: number, base: number, vertical = false): void {
  const gap = (vertical ? w : h) / n;
  for (let i = 0; i < n; i++) {
    const p = vertical
      ? chamfer(x + i * gap + gap * 0.2, y, gap * 0.55, h, 1.5)
      : chamfer(x, y + i * gap + gap * 0.2, w, gap * 0.55, 1.5);
    recess(g, p, shade(base, 0.25), 1.5);
  }
}

/** Diagonal two-colour hazard stripes clipped to a path. */
export function stripes(g: G, path: Path2D, b: Bounds, c1: number, c2: number, width = 9, lit = true): void {
  g.save();
  g.clip(path);
  g.fillStyle = css(c1);
  g.fillRect(b.x - 2, b.y - 2, b.w + 4, b.h + 4);
  g.fillStyle = css(c2);
  const span = b.w + b.h;
  for (let i = -span; i < span; i += width * 2) {
    g.beginPath();
    g.moveTo(b.x + i, b.y + b.h + 2);
    g.lineTo(b.x + i + width, b.y + b.h + 2);
    g.lineTo(b.x + i + width + b.h + 4, b.y - 2);
    g.lineTo(b.x + i + b.h + 4, b.y - 2);
    g.closePath();
    g.fill();
  }
  if (lit) {
    const grad = g.createLinearGradient(b.x, b.y, b.x + b.w * 0.6, b.y + b.h);
    grad.addColorStop(0, 'rgba(255,255,255,0.22)');
    grad.addColorStop(0.6, 'rgba(255,255,255,0)');
    grad.addColorStop(1, 'rgba(0,0,0,0.35)');
    g.fillStyle = grad;
    g.fillRect(b.x - 2, b.y - 2, b.w + 4, b.h + 4);
  }
  g.restore();
}

/** Bright emissive element: soft bloom, saturated edges, white-hot centre along its long axis. */
export function emissive(g: G, path: Path2D, b: Bounds, color: number, o: { blur?: number; heat?: number } = {}): void {
  bloom(g, path, color, o.blur ?? 8, 0.9);
  const horizontal = b.w >= b.h;
  const grad = horizontal
    ? g.createLinearGradient(0, b.y, 0, b.y + b.h)
    : g.createLinearGradient(b.x, 0, b.x + b.w, 0);
  const hot = css(mix(color, 0xffffff, o.heat ?? 0.7));
  grad.addColorStop(0, css(color));
  grad.addColorStop(0.45, hot);
  grad.addColorStop(0.55, hot);
  grad.addColorStop(1, css(shade(color, 0.8)));
  g.fillStyle = grad;
  g.fill(path);
}

/** Round emissive (lens, beacon): bloom + hot radial body. */
export function emissiveDot(g: G, x: number, y: number, r: number, color: number, blur = 10): void {
  bloom(g, disc(x, y, r), color, blur, 0.95);
  const grad = g.createRadialGradient(x - r * 0.2, y - r * 0.2, 0, x, y, r);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.35, css(mix(color, 0xffffff, 0.6)));
  grad.addColorStop(1, css(color));
  g.fillStyle = grad;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fill();
}

/** Blurred copy of a shape (shadow trick: only the blurred shadow lands on the canvas). */
export function bloom(g: G, path: Path2D, color: number, blur: number, alpha = 1): void {
  g.save();
  g.globalAlpha = alpha;
  g.shadowColor = css(color);
  g.shadowBlur = blur;
  g.shadowOffsetX = 10000;
  g.translate(-10000, 0);
  g.fillStyle = css(color);
  g.fill(path);
  g.restore();
}

/** White mask with a soft halo, for additive glow overlays (tinted at runtime). */
export function glowMask(g: G, path: Path2D, blur: number, coreAlpha = 1): void {
  bloom(g, path, 0xffffff, blur, 1);
  bloom(g, path, 0xffffff, blur * 0.45, 1);
  g.fillStyle = `rgba(255,255,255,${coreAlpha})`;
  g.fill(path);
}

/** Radial soft dot. */
export function softDot(g: G, x: number, y: number, r: number, color: number, alpha = 1): void {
  const grad = g.createRadialGradient(x, y, 0, x, y, r);
  grad.addColorStop(0, css(color, alpha));
  grad.addColorStop(0.35, css(color, alpha * 0.55));
  grad.addColorStop(1, css(color, 0));
  g.fillStyle = grad;
  g.fillRect(x - r, y - r, r * 2, r * 2);
}

/** Glossy sphere (upper-left specular) — cores, lenses, hubs. */
export function sphere(g: G, x: number, y: number, r: number, inner: number, outer: number, o: { outline?: boolean; spec?: number } = {}): void {
  if (o.outline !== false) {
    g.fillStyle = css(COLORS.ink);
    g.beginPath();
    g.arc(x, y, r + 2, 0, Math.PI * 2);
    g.fill();
  }
  const grad = g.createRadialGradient(x - r * 0.3, y - r * 0.35, r * 0.05, x, y, r);
  grad.addColorStop(0, css(inner));
  grad.addColorStop(0.55, css(mix(inner, outer, 0.6)));
  grad.addColorStop(1, css(outer));
  g.fillStyle = grad;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fill();
  const spec = o.spec ?? 0.55;
  if (spec > 0) {
    g.fillStyle = `rgba(255,255,255,${spec})`;
    g.beginPath();
    g.ellipse(x - r * 0.38, y - r * 0.42, r * 0.28, r * 0.16, -0.6, 0, Math.PI * 2);
    g.fill();
  }
}

/** Mechanical hub: outlined disc with an inner ring and bolts. */
export function hub(g: G, x: number, y: number, r: number, base: number, bolts = 4): void {
  const p = disc(x, y, r);
  solid(g, p, { x: x - r, y: y - r, w: r * 2, h: r * 2 }, base, { bevel: Math.max(1.5, r * 0.12) });
  const inner = disc(x, y, r * 0.55);
  solid(g, inner, { x: x - r * 0.55, y: y - r * 0.55, w: r * 1.1, h: r * 1.1 }, shade(base, 0.8), { outline: false, bevel: 1.5 });
  g.strokeStyle = css(shade(base, 0.35));
  g.lineWidth = 1.5;
  g.stroke(inner);
  const pts: Pt[] = [];
  for (let i = 0; i < bolts; i++) {
    const a = (i / bolts) * Math.PI * 2 + Math.PI / 4;
    pts.push([x + Math.cos(a) * r * 0.78, y + Math.sin(a) * r * 0.78]);
  }
  rivets(g, pts, base, Math.max(1.4, r * 0.1));
}

/** Small stencilled lettering (e.g. unit numbers). */
export function stencil(g: G, text: string, x: number, y: number, size: number, color: number, alpha = 0.8, rot = 0): void {
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  g.font = `700 ${size}px "Chakra Petch", "Bahnschrift", sans-serif`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = css(color, alpha);
  g.fillText(text, 0, 0);
  g.restore();
}

// ───────────── battle damage ─────────────

/** Jagged crack network: dark fissure with a lit lower edge, optional glowing interior. */
export function cracks(
  g: G,
  rng: Rng,
  x: number,
  y: number,
  len: number,
  o: { branches?: number; angle?: number; glow?: number; width?: number; base?: number } = {},
): void {
  const branches = o.branches ?? 3;
  const width = o.width ?? 2;
  const lines: Pt[][] = [];
  const walk = (sx: number, sy: number, a: number, l: number, depth: number): void => {
    const pts: Pt[] = [[sx, sy]];
    let cx = sx;
    let cy = sy;
    const steps = Math.max(2, Math.round(l / 7));
    for (let i = 0; i < steps; i++) {
      a += rng.float(-0.55, 0.55);
      cx += Math.cos(a) * (l / steps);
      cy += Math.sin(a) * (l / steps);
      pts.push([cx, cy]);
      if (depth < 2 && rng.chance(0.28)) walk(cx, cy, a + rng.sign() * rng.float(0.6, 1.2), l * 0.45, depth + 1);
    }
    lines.push(pts);
  };
  const a0 = o.angle ?? rng.float(0, Math.PI * 2);
  for (let i = 0; i < branches; i++) walk(x, y, a0 + (i / branches) * Math.PI * 2 + rng.float(-0.4, 0.4), len * rng.float(0.6, 1), 0);
  g.save();
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const pts of lines) {
    const p = new Path2D();
    p.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) p.lineTo(pts[i][0], pts[i][1]);
    if (o.glow !== undefined) {
      g.save();
      g.shadowColor = css(o.glow);
      g.shadowBlur = 6;
      g.lineWidth = width + 1.5;
      g.strokeStyle = css(o.glow, 0.9);
      g.stroke(p);
      g.restore();
      g.lineWidth = width * 0.5;
      g.strokeStyle = css(mix(o.glow, 0xffffff, 0.7));
      g.stroke(p);
    } else {
      g.lineWidth = width;
      g.strokeStyle = 'rgba(5,7,10,0.92)';
      g.stroke(p);
      g.save();
      g.translate(0.8, 1);
      g.lineWidth = width * 0.5;
      g.strokeStyle = css(shade(o.base ?? COLORS.steel4, 1.6), 0.45);
      g.stroke(p);
      g.restore();
    }
  }
  g.restore();
}

/** Soot / heat scorch blotch. */
export function scorch(g: G, x: number, y: number, r: number, alpha = 0.6, tint = 0x120c08): void {
  const grad = g.createRadialGradient(x, y, 0, x, y, r);
  grad.addColorStop(0, css(tint, alpha));
  grad.addColorStop(0.6, css(tint, alpha * 0.45));
  grad.addColorStop(1, css(tint, 0));
  g.fillStyle = grad;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fill();
}

/** Bullet dents: small craters with a lit lower-right rim. */
export function dents(g: G, rng: Rng, b: Bounds, n: number, base: number): void {
  for (let i = 0; i < n; i++) {
    const x = rng.float(b.x, b.x + b.w);
    const y = rng.float(b.y, b.y + b.h);
    const r = rng.float(1.6, 3.2);
    g.fillStyle = css(shade(base, 0.3), 0.9);
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = css(shade(base, 1.8), 0.5);
    g.lineWidth = 1;
    g.beginPath();
    g.arc(x, y, r + 0.6, 0.1, Math.PI * 0.9);
    g.stroke();
  }
}

/** Torn-open panel showing the machine's guts: dark cavity, cables, a hot spot. */
export function breach(g: G, rng: Rng, path: Path2D, b: Bounds, base: number, hot: number): void {
  // Ragged rim.
  g.save();
  g.lineWidth = 5;
  g.strokeStyle = css(shade(base, 1.25));
  g.stroke(path);
  g.restore();
  recess(g, path, 0x07090c, 4);
  g.save();
  g.clip(path);
  softDot(g, b.x + b.w * rng.float(0.35, 0.65), b.y + b.h * rng.float(0.45, 0.7), Math.max(b.w, b.h) * 0.55, hot, 0.55);
  const colors = [0xd23b3b, 0xd9a62b, 0x3b7bd2, 0x9aa5b4, 0x2e9b5a];
  for (let i = 0; i < 6; i++) {
    const c = colors[i % colors.length];
    const x0 = b.x + rng.float(0, b.w);
    const x1 = b.x + rng.float(0, b.w);
    g.lineWidth = rng.float(2, 3.4);
    g.strokeStyle = css(COLORS.ink);
    g.beginPath();
    g.moveTo(x0, b.y - 2);
    g.bezierCurveTo(x0 + rng.float(-12, 12), b.y + b.h * 0.6, x1 + rng.float(-12, 12), b.y + b.h * 0.4, x1, b.y + b.h + 2);
    g.stroke();
    g.lineWidth -= 1.2;
    g.strokeStyle = css(c);
    g.stroke();
  }
  g.restore();
  g.save();
  g.lineWidth = 2;
  g.strokeStyle = css(COLORS.ink);
  g.stroke(path);
  g.restore();
}

/** Deterministic art RNG so textures look identical every boot. */
export function artRng(label: string): Rng {
  let h = 2166136261;
  for (let i = 0; i < label.length; i++) h = Math.imul(h ^ label.charCodeAt(i), 16777619);
  return new Rng(h >>> 0);
}

export { css, mix, shade };
